import type { Grid } from './types';
import type { Rng } from './rng';

/** Lectura con índices comprobados: `grid[persona][hora]`. */
export function cellAt(grid: Grid, person: number, hour: number): number {
  const row = grid[person];
  if (row === undefined) throw new RangeError(`persona fuera de rango: ${person}`);
  const value = row[hour];
  if (value === undefined) throw new RangeError(`hora fuera de rango: ${hour}`);
  return value;
}

export function cloneGrid(grid: Grid): Grid {
  return grid.map((row) => [...row]);
}

/** Hora en la que `person` pasa por `room`, o `-1`. */
export function hourOf(grid: Grid, person: number, room: number): number {
  const row = grid[person];
  if (row === undefined) throw new RangeError(`persona fuera de rango: ${person}`);
  return row.indexOf(room);
}

/** Primera hora en la que alguien ocupa `room`, o `-1`. */
export function firstHourOccupied(grid: Grid, room: number, n: number): number {
  for (let h = 0; h < n; h += 1) {
    for (let p = 0; p < n; p += 1) {
      if (cellAt(grid, p, h) === room) return h;
    }
  }
  return -1;
}

/** Última hora en la que alguien ocupa `room`, o `-1`. */
export function lastHourOccupied(grid: Grid, room: number, n: number): number {
  for (let h = n - 1; h >= 0; h -= 1) {
    for (let p = 0; p < n; p += 1) {
      if (cellAt(grid, p, h) === room) return h;
    }
  }
  return -1;
}

/**
 * Cuadrado latino: cada fila es una permutación de las salas (cada persona pasa por
 * cada sala exactamente una vez) y cada columna también (una persona por sala y hora).
 */
export function isLatinSquare(grid: Grid): boolean {
  const n = grid.length;
  if (n === 0) return false;
  for (const row of grid) if (row.length !== n) return false;

  const full = (1 << n) - 1;
  for (let p = 0; p < n; p += 1) {
    let rowMask = 0;
    let colMask = 0;
    for (let h = 0; h < n; h += 1) {
      const inRow = cellAt(grid, p, h);
      const inCol = cellAt(grid, h, p);
      if (inRow < 0 || inRow >= n || inCol < 0 || inCol >= n) return false;
      rowMask |= 1 << inRow;
      colMask |= 1 << inCol;
    }
    if (rowMask !== full || colMask !== full) return false;
  }
  return true;
}

/**
 * Cuadrado latino aleatorio de orden N por backtracking aleatorizado. Con N ≤ 6 el
 * backtracking apenas se dispara y, a diferencia del cuadrado cíclico permutado,
 * alcanza todas las clases de isotopía.
 */
export function randomLatinSquare(n: number, rng: Rng): Grid {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`N inválido: ${n}`);

  const grid: Grid = Array.from({ length: n }, () => new Array<number>(n).fill(-1));
  const rowUsed = new Uint32Array(n);
  const colUsed = new Uint32Array(n);

  const place = (index: number): boolean => {
    if (index === n * n) return true;
    const p = Math.floor(index / n);
    const h = index % n;
    const taken = (rowUsed[p] as number) | (colUsed[h] as number);

    const candidates: number[] = [];
    for (let r = 0; r < n; r += 1) if ((taken & (1 << r)) === 0) candidates.push(r);
    rng.shuffle(candidates);

    for (const r of candidates) {
      (grid[p] as number[])[h] = r;
      rowUsed[p] = (rowUsed[p] as number) | (1 << r);
      colUsed[h] = (colUsed[h] as number) | (1 << r);
      if (place(index + 1)) return true;
      (grid[p] as number[])[h] = -1;
      rowUsed[p] = (rowUsed[p] as number) & ~(1 << r);
      colUsed[h] = (colUsed[h] as number) & ~(1 << r);
    }
    return false;
  };

  if (!place(0))
    throw new Error(`no se pudo construir un cuadrado latino de orden ${n}`);
  return grid;
}
